import { Modal, Box, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import moment from "moment";
import colors from "assets/theme/base/colors";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: `${colors.modal.backGround}`,
  borderRadius: "8px",
  maxWidth: "470px",
  width: "90%",
  overflowY: "auto",
  overflowX: "hidden",
  maxHeight: "90%",
  height: "auto",
  pt: 0,
  "&:focus": {
    outline: "none",
  },
};
const SectionHeader = ({ icon, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
    {icon}
    <Typography
      variant="caption"
      sx={{
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "text.secondary",
        fontSize: "0.8rem",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const SectionCard = ({ children }) => (
  <Box
    sx={{
      bgcolor: "grey.50",
      borderRadius: 2,
      px: 2,
      py: 0.5,
      "& > *:last-child": { borderBottom: "none !important" },
    }}
  >
    {children}
  </Box>
);

const Row = ({ label, value, valueColor, chip }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: "10px",
      borderBottom: "1px solid rgba(0,0,0,0.06)",
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
      {label}
    </Typography>
    {chip ? (
      chip
    ) : (
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, fontSize: "0.8125rem", color: valueColor || "text.primary" }}
      >
        {value}
      </Typography>
    )}
  </Box>
);

const Chip = ({ label, color }) => {
  const colorMap = {
    success: { bg: "rgba(76,175,80,0.12)", text: "#2e7d32" },
    warning: { bg: "rgba(255,152,0,0.12)", text: "#e65100" },
    info: { bg: "rgba(33,150,243,0.12)", text: "#0d47a1" },
    danger: { bg: "rgba(244,67,54,0.12)", text: "#c62828" },
  };
  const c = colorMap[color] || colorMap.info;
  return (
    <Box
      component="span"
      sx={{
        fontSize: "0.72rem",
        fontWeight: 600,
        bgcolor: c.bg,
        color: c.text,
        px: "10px",
        py: "2px",
        borderRadius: "20px",
      }}
    >
      {label}
    </Box>
  );
};

const PriceBadge = ({ label, amount, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem" }}>
      {label}:
    </Typography>
    <Box
      component="span"
      sx={{
        fontSize: "0.72rem",
        fontWeight: 600,
        bgcolor: color === "success" ? "rgba(76,175,80,0.1)" : "rgba(255,152,0,0.1)",
        color: color === "success" ? "success.main" : "warning.main",
        px: "6px",
        py: "1px",
        borderRadius: "4px",
      }}
    >
      ₹{amount?.toLocaleString()}
    </Box>
  </Box>
);

const AmenityLineItem = ({ kind, name, amount, deposit, isLast }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      py: "10px",
      borderBottom: isLast ? "none" : "1px solid rgba(0,0,0,0.06)",
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
      {kind}
    </Typography>
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px" }}>
      <Typography variant="body2" fontWeight={500} sx={{ fontSize: "0.8125rem" }}>
        {name || "—"}
      </Typography>
      <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "flex-end" }}>
        {amount != null && <PriceBadge label="Price" amount={amount} color="success" />}
        {/* {deposit != null && <PriceBadge label="Deposit" amount={deposit} color="warning" />} */}
      </Box>
    </Box>
  </Box>
);

const DateSlotGroup = ({ slotItem }) => {
  const items = [
    ...(slotItem?.amenity_booking_orders || []).map((a) => ({
      kind: "Amenity",
      name: a?.amenity?.name,
      amount: a?.booking_amount,
      deposit: a?.deposit_amount,
      key: `a-${a.id}`,
    })),
    ...(slotItem?.amenity_addons_booking_orders || []).map((a) => ({
      kind: "Add-on",
      name: a?.amenity_addon?.name,
      amount: a?.booking_amount,
      deposit: a?.deposit_amount,
      key: `ad-${a.id}`,
    })),
  ];

  return (
    <Box
      sx={{
        bgcolor: "grey.50",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      {/* Date + slot header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          bgcolor: "rgba(33,150,243,0.06)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8125rem" }}>
          {slotItem?.booking_date ? moment(slotItem.booking_date).format("DD-MM-YYYY") : "—"}
        </Typography>
        <Chip label={slotItem?.slot === 1 ? "Morning" : "Evening"} color="warning" />
      </Box>

      {/* Line items */}
      <Box sx={{ px: 2 }}>
        {items.length > 0 ? (
          items.map((item, i) => (
            <AmenityLineItem
              key={item.key}
              kind={item.kind}
              name={item.name}
              amount={item.amount}
              deposit={item.deposit}
              isLast={i === items.length - 1}
            />
          ))
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.8125rem", py: "10px" }}
          >
            No amenities or add-ons for this date.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default function BookingDetailModal({
  detailOpen,
  handleCloseDetail,
  detailRow,
  getStatusLabel,
}) {
  const dateWiseSlots = detailRow?.amenity_booking_slots;

  const hasDateWiseData = Array.isArray(dateWiseSlots) && dateWiseSlots.length > 0;
  const hasFlatData =
    detailRow?.amenity_booking_orders?.length > 0 ||
    detailRow?.amenity_addons_booking_orders?.length > 0;

  return (
    <Modal open={detailOpen} onClose={handleCloseDetail}>
      <Box sx={style}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={500}>
              Booking details
            </Typography>
          </Box>
          <CloseIcon
            onClick={handleCloseDetail}
            sx={{ cursor: "pointer", color: "text.secondary" }}
          />
        </Box>

        {/* Scrollable body */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            maxHeight: "70vh",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {/* ── Personal details ── */}
          <Box>
            <SectionHeader
              icon={<PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
              label="Personal details"
            />
            <SectionCard>
              <Row
                label="Member number"
                value={detailRow?.member ? `#${detailRow?.member?.member_number}` : "—"}
              />
              <Row label="Name" value={detailRow?.member_name || "—"} />
              <Row
                label="Contact number"
                value={"+91" + " " + detailRow?.member_mobile_number || "—"}
              />
              <Row
                label="Customer type"
                chip={
                  <Chip
                    label={detailRow?.is_guest_customer === 0 ? "Member" : "Non-member"}
                    color="info"
                  />
                }
              />
            </SectionCard>
          </Box>

          {/* ── Booking details ── */}
          <Box>
            <SectionHeader
              icon={<CalendarMonthIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
              label="Booking details"
            />
            <SectionCard>
              <Row label="Occasion" value={detailRow?.occasion?.name || "—"} />
              {/* <Row
                label="Start date & time"
                value={
                  detailRow?.start_date
                    ? `${moment(detailRow?.start_date).format("DD-MM-YYYY")}  ${
                        detailRow?.start_time
                          ? moment(detailRow?.start_time, ["HH:mm:ss", "HH:mm"]).format("hh:mm A")
                          : ""
                      }`
                    : "—"
                }
              />
              <Row
                label="End date & time"
                value={
                  detailRow?.end_date
                    ? `${moment(detailRow?.end_date).format("DD-MM-YYYY")}  ${
                        detailRow?.end_time
                          ? moment(detailRow?.end_time, ["HH:mm:ss", "HH:mm"]).format("hh:mm A")
                          : ""
                      }`
                    : "—"
                }
              /> */}
              <Row
                label="Slot"
                chip={
                  <Chip label={detailRow?.slot === 1 ? "Morning" : "Evening"} color="warning" />
                }
              />
              <Row label="Status" value={getStatusLabel(detailRow?.status)} />
              <Row
                label="Transaction ID"
                value={detailRow?.order_transactions?.[0]?.transaction_id || "—"}
              />
              <Row
                label="Created at"
                value={
                  detailRow?.createdAt
                    ? moment(detailRow?.createdAt).format("DD-MM-YYYY HH:mm:ss")
                    : "—"
                }
              />
            </SectionCard>
          </Box>

          {/* ── Amenities & add-ons (date-wise) ── */}
          {hasDateWiseData && (
            <Box>
              <SectionHeader
                icon={<ApartmentIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                label="Amenities & add-ons"
              />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {dateWiseSlots.map((slotItem, idx) => (
                  <DateSlotGroup key={slotItem.id ?? idx} slotItem={slotItem} />
                ))}
              </Box>
            </Box>
          )}

          {/* ── Amenities & add-ons (flat fallback, old shape) ── */}
          {!hasDateWiseData && hasFlatData && (
            <Box>
              <SectionHeader
                icon={<ApartmentIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                label="Amenities & add-ons"
              />
              <SectionCard>
                {detailRow?.amenity_booking_orders?.map((a, i) => (
                  <AmenityLineItem
                    key={a.id ?? i}
                    kind="Amenity"
                    name={a?.amenity?.name}
                    amount={a?.booking_amount}
                    deposit={a?.deposit_amount}
                    isLast={
                      i === detailRow.amenity_booking_orders.length - 1 &&
                      !detailRow?.amenity_addons_booking_orders?.length
                    }
                  />
                ))}
                {detailRow?.amenity_addons_booking_orders?.map((a, i) => (
                  <AmenityLineItem
                    key={a.id ?? i}
                    kind="Add-on"
                    name={a?.amenity_addon?.name}
                    amount={a?.booking_amount}
                    deposit={a?.deposit_amount}
                    isLast={i === detailRow.amenity_addons_booking_orders.length - 1}
                  />
                ))}
              </SectionCard>
            </Box>
          )}

          {/* ── Payment details ── */}
          <Box>
            <SectionHeader
              icon={<ReceiptIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
              label="Payment details"
            />
            <SectionCard>
              <Row
                label="Subtotal"
                value={
                  detailRow?.subtotal_amount
                    ? `₹${detailRow?.subtotal_amount.toLocaleString()}`
                    : "₹0"
                }
              />

              {/* <Row
                label="Total Deposit"
                value={
                  detailRow?.deposit_amount
                    ? `₹${detailRow?.deposit_amount.toLocaleString()}`
                    : "₹0"
                }
              /> */}
              <Row
                label="Total Discount"
                value={
                  detailRow?.discount_amount
                    ? `- ₹${detailRow?.discount_amount.toLocaleString()}`
                    : "₹0"
                }
                valueColor="success.main"
              />
              <Row
                label="Total amount"
                value={
                  detailRow?.total_amount ? `₹${detailRow?.total_amount.toLocaleString()}` : "₹0"
                }
              />
            </SectionCard>
          </Box>
          {/* {detailRow?.is_settled === 1 && (
            <Box>
              <SectionHeader
                icon={<AccountBalanceWalletIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                label="Settlement Details"
              />
              <SectionCard> */}
          {/* <Row
                  label="Total amount"
                  value={
                    detailRow?.total_amount ? `₹${detailRow?.total_amount.toLocaleString()}` : "₹0"
                  }
                /> */}
          {/* <Row
                  label="Refund amount"
                  value={
                    detailRow?.deposit_amount
                      ? `₹${detailRow?.deposit_amount?.toLocaleString()}`
                      : "₹0"
                  }
                /> */}
          {/* {detailRow?.is_settled === 1 && (
                  <Row
                    label="Extra charge"
                    value={
                      detailRow?.extra_amount
                        ? `+ ₹${detailRow?.extra_amount?.toLocaleString()}`
                        : "₹0"
                    }
                    valueColor={detailRow?.extra_amount ? "error.main" : undefined}
                  />
                )} */}

          {/* <Row
                  label="Settlement amount"
                  value={
                    detailRow?.settlement_amount
                      ? `₹${detailRow.settlement_amount.toLocaleString()}`
                      : "₹0"
                  }
                /> */}
          {/* {detailRow?.is_settled === 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: "12px",
                    }}
                  >
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: "0.875rem" }}>
                      Final amount
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ fontSize: "0.9375rem" }}>
                      {detailRow?.settlement_amount
                        ? `₹${detailRow?.settlement_amount?.toLocaleString()}`
                        : "₹0"}
                    </Typography>
                  </Box>
                )} */}
          {/* </SectionCard> */}

          {/* <Box
                sx={{
                  marginTop: "10px",
                  width: "100%",
                  padding: "10px 14px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: detailRow?.is_settled
                    ? "rgba(25,135,84,0.1)"
                    : "rgba(220,53,69,0.08)",
                  border: detailRow?.is_settled
                    ? "1px solid rgba(25,135,84,0.25)"
                    : "1px solid rgba(220,53,69,0.2)",
                  transition: "all 0.2s ease",
                }}
              >
                {detailRow?.is_settled ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#198754" }} />
                ) : (
                  <AccessTimeIcon sx={{ fontSize: 18, color: "#dc3545" }} />
                )}
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: detailRow?.is_settled ? "#145c38" : "#a71d2a",
                  }}
                >
                  {detailRow?.is_settled ? "Settled" : "Pending settlement"}
                </Typography>
              </Box> */}
          {/* </Box> */}
          {/* )} */}
        </Box>
      </Box>
    </Modal>
  );
}
